// src/pages/Header.jsx
import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Stack,
  Collapse,
  Icon,
  Container,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Link
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/LogoHarmonyPaint.png';
import { supabase } from '../config/supabaseClient';

const Header = () => {
  // Use o hook useDisclosure do Chakra UI para controlar o menu mobile
  const { isOpen, onToggle } = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  
  // Verificar se o usuário está na página inicial
  const isHomePage = location.pathname === '/';

  // Obter informações do usuário ao carregar o componente
  useEffect(() => {
    const fetchUserData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (profileData) {
          setUser({
            ...session.user,
            ...profileData
          });
        } else {
          setUser(session.user);
        }
      }
    };

    fetchUserData();

    // Escutar mudanças na autenticação
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (profileData) {
            setUser({
              ...session.user,
              ...profileData
            });
          } else {
            setUser(session.user);
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
        }
      }
    );

    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Componente para link de navegação condicional
  const ConditionalNavLink = ({ to, children, ...props }) => {
    if (isHomePage) {
      return (
        <Box
          as={ScrollLink}
          to={to}
          smooth={true}
          duration={500}
          cursor="pointer"
          {...props}
        >
          {children}
        </Box>
      );
    } else {
      return (
        <Box
          as={RouterLink}
          to={`/#${to}`}
          cursor="pointer"
          {...props}
        >
          {children}
        </Box>
      );
    }
  };

  // Componente para link de navegação condicional (versão mobile)
  const ConditionalMobileNavLink = ({ to, children, ...props }) => {
    if (isHomePage) {
      return (
        <Box
          as={ScrollLink}
          to={to}
          smooth={true}
          duration={500}
          onClick={onToggle}
          cursor="pointer"
          {...props}
        >
          {children}
        </Box>
      );
    } else {
      return (
        <Box
          as={RouterLink}
          to={`/#${to}`}
          onClick={onToggle}
          cursor="pointer"
          {...props}
        >
          {children}
        </Box>
      );
    }
  };

  return (
    <Box as="header" position="fixed" w="100%" zIndex={1000} bg="white" boxShadow="sm">
      <Container maxW="1200px">
        <Flex py={4} align="center" justify="space-between">
          {/* Logo */}
          <Flex align="center" as={RouterLink} to="/" _hover={{ textDecoration: 'none' }}>
            <Box as="img" src={logo} alt="Draw Your Goods Logo" h="40px" mr={2} />
            <Text fontSize="xl" fontWeight="bold" display={{ base: 'none', sm: 'block' }}>
              <Text as="span" color="brand.primary">Draw</Text>
              <Text as="span" color="brand.secondary"> Goods</Text>
            </Text>
          </Flex>

          {/* Desktop Navigation */}
          <Flex display={{ base: 'none', md: 'flex' }} align="center">
            <Stack direction="row" spacing={8} align="center">
              {!user ? (
                <>
                  <ConditionalNavLink
                    to="como-funciona"
                    fontWeight="500"
                    color="gray.700"
                    px={2}
                    py={1}
                    _hover={{
                      color: 'brand.primary',
                      textDecoration: 'none',
                    }}
                  >
                    Como Funciona
                  </ConditionalNavLink>
                  <ConditionalNavLink
                    to="exemplos"
                    fontWeight="500"
                    color="gray.700"
                    px={2}
                    py={1}
                    _hover={{
                      color: 'brand.primary',
                      textDecoration: 'none',
                    }}
                  >
                    Exemplos
                  </ConditionalNavLink>
                  <ConditionalNavLink
                    to="precos"
                    fontWeight="500"
                    color="gray.700"
                    px={2}
                    py={1}
                    _hover={{
                      color: 'brand.primary',
                      textDecoration: 'none',
                    }}
                  >
                    Preços
                  </ConditionalNavLink>
                  <ConditionalNavLink
                    to="faq"
                    fontWeight="500"
                    color="gray.700"
                    px={2}
                    py={1}
                    _hover={{
                      color: 'brand.primary',
                      textDecoration: 'none',
                    }}
                  >
                    FAQ
                  </ConditionalNavLink>
                  <Button
                    as={RouterLink}
                    to="/login"
                    bgGradient="linear(to-r, brand.primary, brand.secondary)"
                    color="white"
                    _hover={{
                      transform: 'translateY(-3px)',
                      boxShadow: '0 5px 15px rgba(165, 180, 232, 0.4)',
                    }}
                    borderRadius="full"
                    px={6}
                  >
                    Começar
                  </Button>
                </>
              ) : (
                <>
                  <Text color="gray.700" fontWeight="medium">
                    {user.email}
                  </Text>
                  <Text color="gray.700" fontWeight="bold">
                    Créditos: <Text as="span" bg="green.100" px={2} py={1} borderRadius="md">{user.creditos || 0}</Text>
                  </Text>
                  <Menu>
                    <MenuButton as={Button} variant="ghost" borderRadius="full">
                      <Avatar size="sm" name={user.name || user.email} src={user.photo_url} />
                    </MenuButton>
                    <MenuList>
                      <MenuItem as={RouterLink} to="/dashboard">Dashboard</MenuItem>
                      <MenuItem as={RouterLink} to="/dashboard/meu-cupom">Meu Cupom</MenuItem>
                      <MenuItem onClick={handleLogout}>Sair</MenuItem>
                    </MenuList>
                  </Menu>
                </>
              )}
            </Stack>
          </Flex>

          {/* Mobile Navigation Toggle */}
          <Flex display={{ base: 'flex', md: 'none' }} align="center">
            {user && (
              <Text mr={4} fontSize="sm" fontWeight="medium">
                {user.email}
              </Text>
            )}
            <Icon
              as={isOpen ? CloseIcon : HamburgerIcon}
              w={6}
              h={6}
              onClick={onToggle}
              cursor="pointer"
            />
          </Flex>
        </Flex>

        {/* Mobile Navigation Menu */}
        <Collapse in={isOpen} animateOpacity>
          <Box
            py={4}
            display={{ md: 'none' }}
            bg="white"
            boxShadow="md"
          >
            <Stack as="nav" spacing={4}>
              {!user ? (
                <>
                  <ConditionalMobileNavLink
                    to="como-funciona"
                    fontWeight="500"
                    color="gray.700"
                    px={4}
                    py={2}
                    _hover={{
                      color: 'brand.primary',
                      bg: 'gray.50',
                    }}
                  >
                    Como Funciona
                  </ConditionalMobileNavLink>
                  <ConditionalMobileNavLink
                    to="exemplos"
                    fontWeight="500"
                    color="gray.700"
                    px={4}
                    py={2}
                    _hover={{
                      color: 'brand.primary',
                      bg: 'gray.50',
                    }}
                  >
                    Exemplos
                  </ConditionalMobileNavLink>
                  <ConditionalMobileNavLink
                    to="precos"
                    fontWeight="500"
                    color="gray.700"
                    px={4}
                    py={2}
                    _hover={{
                      color: 'brand.primary',
                      bg: 'gray.50',
                    }}
                  >
                    Preços
                  </ConditionalMobileNavLink>
                  <ConditionalMobileNavLink
                    to="faq"
                    fontWeight="500"
                    color="gray.700"
                    px={4}
                    py={2}
                    _hover={{
                      color: 'brand.primary',
                      bg: 'gray.50',
                    }}
                  >
                    FAQ
                  </ConditionalMobileNavLink>
                  <Button
                    as={RouterLink}
                    to="/signup"
                    bgGradient="linear(to-r, brand.primary, brand.secondary)"
                    color="white"
                    borderRadius="full"
                    mx={4}
                    onClick={onToggle}
                  >
                    Começar
                  </Button>
                </>
              ) : (
                <>
                  <Box px={4} py={2}>
                    <Text fontWeight="bold">
                      Créditos: <Text as="span" bg="green.100" px={2} py={1} borderRadius="md">{user.creditos || 0}</Text>
                    </Text>
                  </Box>
                  <Box
                    as={RouterLink}
                    to="/dashboard"
                    fontWeight="500"
                    color="gray.700"
                    px={4}
                    py={2}
                    cursor="pointer"
                    onClick={onToggle}
                    _hover={{
                      color: 'brand.primary',
                      bg: 'gray.50',
                    }}
                  >
                    Dashboard
                  </Box>
                  <Box
                    as={RouterLink}
                    to="/dashboard/meu-cupom"
                    fontWeight="500"
                    color="gray.700"
                    px={4}
                    py={2}
                    cursor="pointer"
                    onClick={onToggle}
                    _hover={{
                      color: 'brand.primary',
                      bg: 'gray.50',
                    }}
                  >
                    Meu Cupom
                  </Box>
                  <Box
                    onClick={() => {
                      handleLogout();
                      onToggle();
                    }}
                    fontWeight="500"
                    color="gray.700"
                    px={4}
                    py={2}
                    cursor="pointer"
                    _hover={{
                      color: 'red.500',
                      bg: 'gray.50',
                    }}
                  >
                    Sair
                  </Box>
                </>
              )}
            </Stack>
          </Box>
        </Collapse>
      </Container>
    </Box>
  );
};

export default Header;